'use client';

import { useEffect } from 'react';
import { USDC_SPOT_MARKET_INDEX, SOL_SPOT_MARKET_INDEX } from '@drift/common';
import type { UISerializableDepositRecord } from '@drift/common';
import type { VersionedTransactionResponse } from '@solana/web3.js';
import useAppEventEmitter from './useAppEventEmitter';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';

const FUN_XYZ_SIGNER_WHITELIST = new Set([
	'zApVWDs3nSychNnUXSS2czhY78Ycopa15zELrK2gAdM',
	'4B62MS5gxpRZ2hwkGCNAAayA5f7LYZRW4z1ASSfU3SXo',
]);

const FUN_XYZ_TARGET_MARKETS = new Set([
	USDC_SPOT_MARKET_INDEX,
	SOL_SPOT_MARKET_INDEX,
]);

const MARKET_TO_ASSET: Record<number, string> = {
	[USDC_SPOT_MARKET_INDEX]: 'USDC',
	[SOL_SPOT_MARKET_INDEX]: 'SOL',
};

function extractAccountPubkeys(
	res: VersionedTransactionResponse | null
): string[] {
	if (!res?.transaction?.message) return [];
	const { transaction, meta } = res;
	const accountKeys = transaction.message.getAccountKeys(
		meta?.loadedAddresses
			? { accountKeysFromLookups: meta.loadedAddresses }
			: undefined
	);
	return accountKeys
		.keySegments()
		.flat()
		.map((pk) => pk.toBase58());
}

/**
 * Passively listens for Fun.xyz deposits (USDC or SOL). Shows a success notification
 * when a deposit with a Fun.xyz signer is detected.
 */
export default function useFunXyzDepositWatcher() {
	const appEventEmitter = useAppEventEmitter();
	const connection = useDriftStore((s) => s.connection.current);

	useEffect(() => {
		if (!connection) return;

		const handler = async (payload?: {
			depositRecord: UISerializableDepositRecord;
		}) => {
			if (!payload?.depositRecord) return;

			const { depositRecord } = payload;
			if (!FUN_XYZ_TARGET_MARKETS.has(depositRecord.marketIndex)) return;

			try {
				const res = await connection.getTransaction(depositRecord.txSig, {
					maxSupportedTransactionVersion: 0,
					commitment: 'confirmed',
				});
				const accountPubkeys = extractAccountPubkeys(res);
				const hasFunXyzSigner = accountPubkeys.some((pk) =>
					FUN_XYZ_SIGNER_WHITELIST.has(pk)
				);
				if (hasFunXyzSigner) {
					const asset = MARKET_TO_ASSET[depositRecord.marketIndex] ?? 'deposit';
					notify({
						type: 'success',
						message: `Your ${asset} deposit has been received`,
					});
				}
			} catch {
				// Silently ignore fetch errors; user can check balance
			}
		};

		appEventEmitter.on('newDepositRecord', handler);
		return () => {
			appEventEmitter.removeListener('newDepositRecord', handler);
		};
	}, [appEventEmitter, connection]);
}
